import { FormikContextType, FormikValues, useFormikContext } from 'formik';
import { createElement, FC, useEffect } from 'react';
import { AnySchema } from 'yup';
import { FormFieldProps } from '../fields';

type WrappedProps<Props = FormFieldProps<never>> = {
  isHidden?: (values: FormikValues) => boolean;
} & Props;

type Params = {
  props: WrappedProps;
  formikContext: FormikContextType<FormikValues>;
  resolvedEmptyValue: unknown;
  show: boolean;
};

export function FormFieldWrapper<Props extends FormFieldProps<never>>(
  WrappedComponent: FC<Props>,
  emptyValueOrGenerator: unknown | ((props: Props) => unknown) = ''
) {
  return (props: WrappedProps<Props>) => {
    const formikContext = useFormikContext<FormikValues>();

    const resolvedEmptyValue = isEmptyValueGenerator<Props>(emptyValueOrGenerator)
      ? emptyValueOrGenerator(props)
      : emptyValueOrGenerator;

    useInitializeField(props.name, formikContext, resolvedEmptyValue);
    const isInitialized = checkIfPropExists(props.name, formikContext.values);
    const hidden = !!props.isHidden?.(formikContext.values);
    const show = isInitialized && !hidden;

    useRegisterAndResetField({
      props,
      formikContext,
      resolvedEmptyValue,
      show
    });

    if (!show) {
      return null;
    }

    const newProps = {
      ...props
    };

    delete newProps.isHidden;

    return createElement(WrappedComponent, newProps as Props);
  };
}

export const wrapValidation = (validation: AnySchema) => (value: unknown) => {
  try {
    const passedValue = castEmptyValueToUndefinedForValidation(value);
    validation.validateSync(passedValue);
  } catch (e) {
    return (e as Error).message;
  }
};

const useInitializeField = (
  name: string,
  { values, setFieldValue, initialValues }: FormikContextType<FormikValues>,
  emptyValue: unknown
) => {
  useEffect(() => {
    if (!checkIfPropExists(name, values)) {
      initialValues[name] = emptyValue;
      setFieldValue(name, emptyValue);
    }
  }, []);
};

const useRegisterAndResetField = ({ props, formikContext, resolvedEmptyValue, show }: Params) => {
  const { name, validation } = props;
  const { setFieldValue, registerField } = formikContext;

  useEffect(() => {
    if (!show) {
      setFieldValue(name, resolvedEmptyValue);
    } else {
      const validate = validation ? wrapValidation(validation) : undefined;
      registerField(name, { validate });
    }
  }, [show]);
};

const isEmptyValueGenerator = <Props>(
  emptyValueOrFunction: unknown
): emptyValueOrFunction is (props: Props) => unknown => typeof emptyValueOrFunction === 'function';

const castEmptyValueToUndefinedForValidation = (value: unknown) => {
  if (value === '') {
    return undefined;
  }
  return value;
};

const checkIfPropExists = (name: string, values: FormikValues): boolean => {
  const parts = name.split('.');

  if (parts[0] === name) {
    return parts[0] in values && values[parts[0]] !== undefined;
  }

  if (parts[0] in values) {
    const current = values[parts[0]];
    return checkIfPropExists(parts.slice(1).join('.'), current);
  }

  return false;
};
